import { describeSdkError, isTransferCancelled, reportAsyncError } from "@istari-frontend/shared";
import { V3Models } from "@istari/istari-client";
import { useCallback } from "react";
import { toast } from "sonner";

import { usePatchModelControlTags } from "~/api/useControlTags";
import { useAssignInfosecLevel } from "~/api/useInfosecLevels";

import { canResumeUpload, registerUploadPayload, subscribeUpload } from "../data-source/upload-payloads";
import {
  awaitUploadSessionReady,
  captureUploadSession,
  isUploadSessionCurrent,
} from "../data-source/upload-session-callbacks";
import { useCreateResource } from "../data-source/use-create-resource";
import {
  dismissUploadBatch,
  type FileUploadState,
  fileUploadState,
  getUploadBatch,
  resetUploadBatch,
  retryOperations,
  setUploadBatchProgress,
  startUploadBatch,
  type UploadingFile,
  type UploadProgress,
} from "./upload-batch-progress";
import { retainUploadRetry } from "./upload-retry";

export type { UploadingFile, FileUploadState } from "./upload-batch-progress";

type FileWithMetadata = {
  displayName: string;
  extension: string;
  externalIdentifier: string;
  file: File;
  tags: string[];
  resourceType: V3Models.ResourceCreateTypeDto;
  infosecLevelId?: string | null;
  /** Slash-separated folder the file was picked from, carried untouched so `onComplete` can stage it. */
  originPath?: string | null;
};

type UseUploadResourceOptions = {
  onComplete?: (result: { fileIds: string[]; resourceIds: string[]; files: UploadingFile[] }) => void;
  onError?: (error: Error, file: UploadingFile) => void;
};

export function resetUploadStateForTests() {
  resetUploadBatch();
}

export function useUploadResource(options: UseUploadResourceOptions = {}) {
  const { onComplete, onError } = options;
  const createResource = useCreateResource();
  const patchModelControlTags = usePatchModelControlTags();
  const assignModelInfosecLevel = useAssignInfosecLevel();
  const uploadSingleFile = useCallback(
    async (file: UploadingFile): Promise<UploadingFile> => {
      const session = captureUploadSession();
      try {
        const data: V3Models.ResourceDto = await createResource.mutateOperationAsync({
          operationId: file.id,
          displayName: file.displayName,
          description: "",
          externalIdentifier: file.externalIdentifier,
          resourceType: file.resourceType,
        });
        const serverFileId = data.fileId || data.resourceId;
        const followUpReady = isUploadSessionCurrent(session)
          ? await awaitUploadSessionReady(session).then(
              () => true,
              () => false,
            )
          : false;
        if (followUpReady && isUploadSessionCurrent(session) && file.tags?.length > 0) {
          patchModelControlTags.mutate({
            modelId: data.resourceId,
            controlTagIdsActionTypes: file.tags.map((tagId) => ({ ids: [tagId], action: "set" as const })),
          });
        }
        if (followUpReady && isUploadSessionCurrent(session) && file.infosecLevelId) {
          assignModelInfosecLevel.mutate({ type: "model", id: data.resourceId, infosecLevelId: file.infosecLevelId });
        }
        return {
          ...file,
          state: "success",
          serverFileId,
          resourceId: data.resourceId,
          fileRevisionId: data.fileRevisionId,
        };
      } catch (error) {
        if (isTransferCancelled(error)) return { ...file, state: "cancelled" };
        reportAsyncError("upload-resource", error);
        const errorMessage = await describeSdkError(error, "Upload failed. Please try again.");
        if (isUploadSessionCurrent(session)) {
          try {
            await onError?.(error instanceof Error ? error : new Error(errorMessage), file);
          } catch (callbackError) {
            reportAsyncError("upload-error-callback", callbackError);
          }
        }
        return { ...file, state: "error", errorMessage };
      }
    },
    [createResource, patchModelControlTags, assignModelInfosecLevel, onError],
  );
  const uploadFiles = useCallback(
    (filesWithMetadata: FileWithMetadata[]) => {
      if (filesWithMetadata.length === 0) return Promise.resolve();
      const batchId = startUploadBatch();
      const newFiles: UploadingFile[] = filesWithMetadata.map(({ file, ...metadata }) => {
        const id = registerUploadPayload(file, (operationId) => updateFileProgress(operationId, "uploading", batchId));
        return { ...metadata, tags: [...metadata.tags], id, state: "pending", originPath: metadata.originPath ?? null };
      });
      setUploadBatchProgress(buildProgress([...(getUploadBatch()?.progress?.files ?? []), ...newFiles]));
      newFiles.forEach((file) => observeFileProgress(file.id, batchId));
      return completeBatch(newFiles, batchId, uploadSingleFile, onComplete);
    },
    [uploadSingleFile, onComplete],
  );
  const dismissToast = useCallback(() => dismissUploadBatch(), []);
  return { uploadFiles, dismissToast };
}

function buildProgress(files: UploadingFile[]): UploadProgress {
  const states = files.map(fileUploadState);
  const completedCount = states.filter((state) => state !== "pending" && state !== "uploading").length;
  const hasInFlight = completedCount !== files.length;
  const successCount = states.filter((state) => state === "success").length;
  const errorCount = states.filter((state) => state === "error").length;
  const cancelledCount = states.filter((state) => state === "cancelled").length;
  const overallState = hasInFlight
    ? "uploading"
    : cancelledCount === files.length
      ? "cancelled"
      : successCount === files.length
        ? "success"
        : errorCount === files.length
          ? "error"
          : "partial";
  return { files, completedCount, totalCount: files.length, overallState };
}

function observeFileProgress(id: string, batchId: string) {
  subscribeUpload(id, (snapshot) => {
    const batch = getUploadBatch();
    if (batch?.id !== batchId || !batch.progress) return;
    setUploadBatchProgress(
      buildProgress(batch.progress.files.map((file) => (file.id === id ? { ...file, snapshot } : file))),
    );
  });
}

function updateFileProgress(id: string, state: FileUploadState, batchId: string) {
  const batch = getUploadBatch();
  if (batch?.id !== batchId || !batch.progress) return;
  setUploadBatchProgress(
    buildProgress(batch.progress.files.map((file) => (file.id === id ? { ...file, state } : file))),
  );
}

async function completeBatch(
  files: UploadingFile[],
  batchId: string,
  upload: (file: UploadingFile) => Promise<UploadingFile>,
  onComplete: UseUploadResourceOptions["onComplete"],
) {
  const session = captureUploadSession();
  const results = await Promise.all(
    files.map(async (file) => {
      const result = await upload(file);
      if (result.state === "error" && getUploadBatch()?.id === batchId) {
        retainUploadRetry(
          file.id,
          () => {
            updateFileProgress(file.id, "pending", batchId);
            observeFileProgress(file.id, batchId);
            return completeBatch([file], batchId, upload, onComplete);
          },
          (retry) => {
            if (retry) retryOperations.set(file.id, retry);
            else retryOperations.delete(file.id);
            const current = getUploadBatch()?.progress;
            if (current) setUploadBatchProgress({ ...current });
          },
        );
      }
      const batch = getUploadBatch();
      if (batch?.id === batchId && batch.progress) {
        setUploadBatchProgress(
          buildProgress(
            batch.progress.files.map((row) =>
              row.id === file.id
                ? {
                    ...result,
                    snapshot:
                      row.snapshot && row.snapshot.state !== "pending" && row.snapshot.state !== "uploading"
                        ? row.snapshot
                        : result.state === "error"
                          ? { state: "error", canResume: canResumeUpload(file.id) }
                          : { state: result.state },
                  }
                : row,
            ),
          ),
        );
      }
      return result;
    }),
  );
  const fileIds = results.flatMap((file) => (file.serverFileId ? [file.serverFileId] : []));
  const resourceIds = results.flatMap((file) => (file.resourceId ? [file.resourceId] : []));
  if (isUploadSessionCurrent(session) && fileIds.length > 0) {
    try {
      await awaitUploadSessionReady(session);
      await onComplete?.({ fileIds, resourceIds, files: results });
    } catch (error) {
      if (isTransferCancelled(error)) return;
      reportAsyncError("upload-complete-callback", error);
      toast.error("Files uploaded, but the follow-up step could not finish. Do not upload them again.");
    }
  }
}
